import React from 'react';
import { Box, Paper, Typography, Popover } from '@mui/material';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import * as geojson from 'geojson';
import { useUnitSystem } from '../hooks/useUnitSystem';
import { formatFuelLoad } from '../utils/units';

/**
 * The three chemical-retardant efficacy channels ride in `fuelCharacteristics`
 * alongside the physical fuel properties. They read as multipliers, not
 * quantities, so they get their own labels — and at 1.0 they mean "no
 * retardant here", which is noise on every untreated polygon of a tile that
 * happens to contain a treated one, so they are hidden at that value.
 */
const RETARDANT_CHARACTERISTIC_LABELS: Record<string, string> = {
  retardant_combustion_efficiency: 'Retardant: heat-release rate',
  retardant_available_fuel_fraction: 'Retardant: available fuel',
  retardant_ignitability: 'Retardant: ignitability',
};

/**
 * Araucaria stands carry their age. Stands with no planting or fire date come
 * through as effectively infinite, so the figure is capped for display.
 */
const STAND_AGE_KEY = 'stand_age_years';
const STAND_AGE_CAP_YEARS = 60;

function formatCharacteristic(key: string, value: number | undefined): string {
  if (key in RETARDANT_CHARACTERISTIC_LABELS) {
    const shown = typeof value === 'number' ? `${Math.round(value * 100)}% of untreated` : 'N/A';
    return `${RETARDANT_CHARACTERISTIC_LABELS[key]}: ${shown}`;
  }
  if (key === STAND_AGE_KEY) {
    const shown =
      typeof value !== 'number'
        ? 'N/A'
        : value > STAND_AGE_CAP_YEARS
          ? `${STAND_AGE_CAP_YEARS}+ years`
          : `${value.toFixed(1)} years`;
    return `Stand age: ${shown}`;
  }
  return `${key.replace(/_/g, ' ')}: ${typeof value === 'number' ? value.toFixed(2) : 'N/A'}`;
}

interface FuelLoadLayerPopoverProps {
  feature: geojson.Feature | null;
  anchorPosition: { top: number; left: number } | null;
  onClose: () => void;
}

const FuelLoadLayerPopover: React.FC<FuelLoadLayerPopoverProps> = ({
  feature,
  anchorPosition,
  onClose,
}) => {
  const unitSystem = useUnitSystem();
  const open = Boolean(feature && anchorPosition);

  const layerType = feature?.properties?.layerType as string | undefined;
  const isFuelAge = layerType === 'fuelAge';

  const strata = isFuelAge
    ? []
    : [
        { key: 'surface', label: 'Surface' },
        { key: 'elevated', label: 'Elevated' },
        { key: 'bark', label: 'Bark' },
        { key: 'canopy', label: 'Canopy' },
      ];

  const strataBreakdown = isFuelAge
    ? undefined
    : (feature?.properties?.strataBreakdown as Record<string, number | undefined> | undefined);

  const stratifiedValues = strata
    .map((stratum) => {
      const value = strataBreakdown?.[stratum.key];
      return {
        ...stratum,
        value: typeof value === 'number' ? value : null,
      };
    })
    .filter((entry) => entry.value !== null && !Number.isNaN(entry.value as number));

  const totalFromStrata = stratifiedValues.reduce((sum, stratum) => sum + (stratum.value ?? 0), 0);
  const rawTotal = feature?.properties?.fuelLoad;
  const totalFuelLoad = typeof rawTotal === 'number' ? rawTotal : totalFromStrata || null;
  const rawFuelAge = feature?.properties?.fuelAge;
  const fuelCharacteristics = isFuelAge
    ? undefined
    : (feature?.properties?.fuelCharacteristics as Record<string, number | undefined> | undefined);
  const formattedFuelAge =
    typeof rawFuelAge === 'number'
      ? rawFuelAge > 20
        ? '20+'
        : rawFuelAge.toFixed(1)
      : rawFuelAge ?? 'N/A';

  if (!feature) return null;

  return (
    <Popover
      open={open}
      anchorReference="anchorPosition"
      anchorPosition={anchorPosition || undefined}
      onClose={onClose}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'center',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'center',
      }}
    >
      <Paper sx={{ p: 2, maxWidth: 260, bgcolor: '#212b37', color: 'white' }}>
        {isFuelAge ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <AccessTimeIcon fontSize="small" />
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                Fuel Age
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ fontSize: 14 }}>
              Overall fuel age: {formattedFuelAge} years
            </Typography>
          </Box>
        ) : (
          <>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <LocalFireDepartmentIcon fontSize="small" />
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                Fuel Load
              </Typography>
            </Box>

            {stratifiedValues.length > 0 ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                {stratifiedValues.map(({ key, label, value }) => (
                  <Typography key={key} variant="body2">
                    {label}: {formatFuelLoad(value as number, unitSystem)}
                  </Typography>
                ))}


                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                  Total: {totalFuelLoad !== null ? formatFuelLoad(totalFuelLoad, unitSystem) : 'N/A'}
                </Typography>

                {fuelCharacteristics && Object.keys(fuelCharacteristics).length > 0 && (
                  <>
                    <Box sx={{ borderTop: '1px solid rgba(255,255,255,0.2)', my: 1 }} />
                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                      Fuel Characteristics
                    </Typography>
                    {Object.entries(fuelCharacteristics)
                      .filter(
                        ([key, value]) =>
                          !(key in RETARDANT_CHARACTERISTIC_LABELS && value === 1)
                      )
                      .map(([key, value]) => (
                        <Typography key={key} variant="body2">
                          {formatCharacteristic(key, value)}
                        </Typography>
                      ))}
                  </>
                )}
              </Box>
            ) : (
              <Typography variant="body2">
                No strata data available.
              </Typography>
            )}
          </>
        )}
      </Paper>
    </Popover>
  );
};

export default FuelLoadLayerPopover;
